// Nouns — gender, articles, plurals, diminutives, plus a starter vocabulary
// table with guessed gender. Ported from the legacy views.nouns; VOCABULARY is
// imported directly (old shape: { es, en, cat }).
import { VOCABULARY } from "../data/index.js";
import { renderGuideSections } from "../ui/guideSections.js";

const FEM_OVERRIDES = new Set([
  "casa", "agua", "comida", "familia", "ciudad", "vida", "noche", "semana", "hora",
]);
const MASC_OVERRIDES = new Set([
  "día", "mapa", "problema", "tema", "sistema", "clima", "idioma",
]);

function guessGender(w) {
  if (FEM_OVERRIDES.has(w)) return { g: "f", note: "(la)" };
  if (MASC_OVERRIDES.has(w)) return { g: "m", note: "(el — exception)" };
  if (
    w.endsWith("a") || w.endsWith("ción") || w.endsWith("sión") ||
    w.endsWith("dad") || w.endsWith("tad") || w.endsWith("umbre")
  )
    return { g: "f", note: "(la)" };
  if (
    w.endsWith("o") || w.endsWith("or") || w.endsWith("aje") ||
    w.endsWith("ma") || w.endsWith("s")
  )
    return { g: "m", note: "(el)" };
  return { g: "?", note: "(check)" };
}

export function nounsView(app) {
  const nouns = VOCABULARY.filter((v) => v.cat === "noun");

  const vocabTable = `
    <div class="bg-white rounded-2xl border border-sage-100 p-5 mt-6">
      <h3 class="text-lg font-bold text-sage-800 mb-3">Starter Noun Vocabulary</h3>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3">Spanish</th>
              <th class="py-2 px-3">Gender</th>
              <th class="py-2 px-3">English</th>
            </tr>
          </thead>
          <tbody>
            ${nouns
              .map((n) => {
                const { g, note } = guessGender(n.es);
                const color = g === "f" ? "text-pink-600" : g === "m" ? "text-blue-600" : "text-sage-500";
                return `<tr class="border-b border-sage-50 last:border-0">
                <td class="py-2 pr-3 font-medium text-sage-800">${n.es}</td>
                <td class="py-2 px-3 ${color} font-semibold">${note}</td>
                <td class="py-2 px-3 text-sage-700">${n.en}</td>
              </tr>`;
              })
              .join("")}
          </tbody>
        </table>
      </div>
    </div>
  `;

  renderGuideSections(
    app,
    "Nouns",
    "Spanish nouns have grammatical gender (masculine or feminine) and number (singular or plural). The gender of a noun controls the form of articles and adjectives that go with it.",
    [
      {
        title: "1. Gender — Masculine vs Feminine",
        body: `
          <p class="text-sage-700 mb-3">Most nouns ending in <b>-o</b> are masculine; most ending in <b>-a</b> are feminine. But there are reliable extra clues and a small set of exceptions worth memorising.</p>
          <ul class="text-sage-700 text-sm space-y-1 mb-3 list-disc pl-5">
            <li><b>Masculine endings:</b> -o, -or, -aje, -ma (Greek origin), -s. <i>el libro, el color, el viaje, el problema, el lunes.</i></li>
            <li><b>Feminine endings:</b> -a, -ción, -sión, -dad, -tad, -umbre. <i>la casa, la canción, la ciudad, la libertad, la costumbre.</i></li>
            <li><b>Common exceptions:</b> <i>el día, el mapa, el problema, el tema, el sistema, el idioma</i> (masculine despite ending in -a); <i>la mano, la foto, la moto</i> (feminine despite ending in -o).</li>
            <li><b>Same word, both genders:</b> <i>el/la estudiante, el/la artista</i> — gender depends on the person.</li>
          </ul>
        `,
      },
      {
        title: "2. Articles — el / la / un / una",
        body: `
          <p class="text-sage-700 mb-3">Articles always agree with the noun in gender and number.</p>
          <div class="overflow-x-auto"><table class="w-full text-sm mb-3">
            <thead><tr class="text-left text-xs uppercase tracking-wide text-sage-500 border-b border-sage-100">
              <th class="py-2 pr-3"></th><th class="py-2 px-3">Masculine</th><th class="py-2 px-3">Feminine</th>
            </tr></thead>
            <tbody>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite singular</td><td class="py-2 px-3"><b>el</b> libro</td><td class="py-2 px-3"><b>la</b> casa</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Definite plural</td><td class="py-2 px-3"><b>los</b> libros</td><td class="py-2 px-3"><b>las</b> casas</td></tr>
              <tr class="border-b border-sage-50"><td class="py-2 pr-3 font-medium">Indefinite singular</td><td class="py-2 px-3"><b>un</b> libro</td><td class="py-2 px-3"><b>una</b> casa</td></tr>
              <tr><td class="py-2 pr-3 font-medium">Indefinite plural</td><td class="py-2 px-3"><b>unos</b> libros</td><td class="py-2 px-3"><b>unas</b> casas</td></tr>
            </tbody>
          </table></div>
          <p class="text-sage-700 text-sm"><b>Heads-up:</b> feminine nouns starting with a stressed <b>a-/ha-</b> take <b>el</b>/<b>un</b> in the singular for pronunciation — <i>el agua, el águila, un hacha</i> — but stay feminine: <i>el agua fría</i>.</p>
        `,
      },
      {
        title: "3. Plurals",
        body: `
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li>Ends in a vowel → add <b>-s</b>: <i>casa → casas, libro → libros</i>.</li>
            <li>Ends in a consonant → add <b>-es</b>: <i>ciudad → ciudades, color → colores</i>.</li>
            <li>Ends in <b>-z</b> → change to <b>-ces</b>: <i>luz → luces, vez → veces</i>.</li>
            <li>Ends in stressed vowel + s → add <b>-es</b>: <i>mes → meses, país → países</i>.</li>
            <li>Watch the accent: <i>canción → canciones</i> (accent drops), <i>examen → exámenes</i> (accent added to keep stress).</li>
          </ul>
        `,
      },
      {
        title: "4. Diminutives & Augmentatives",
        body: `
          <p class="text-sage-700 mb-2">Spanish loves modifying nouns with suffixes for size, affection, or emphasis.</p>
          <ul class="text-sage-700 text-sm space-y-1 list-disc pl-5">
            <li><b>-ito / -ita</b> (small, cute): <i>perro → perrito, casa → casita</i>.</li>
            <li><b>-illo / -illa</b> (small): <i>palo → palillo</i> (toothpick), <i>cigarro → cigarrillo</i>.</li>
            <li><b>-ón / -ona</b> (big): <i>casa → casona, hombre → hombrón</i>.</li>
            <li><b>-azo</b> (whack with): <i>codo → codazo</i> (elbow jab), <i>puerta → portazo</i> (door slam).</li>
          </ul>
        `,
      },
    ],
    "",
    vocabTable
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
